/**
 * Generate a campaign proposal PDF using browser print.
 * Opens a new window with styled HTML and triggers print dialog (Save as PDF).
 * Fully supports Korean and all Unicode characters.
 */
export function generateCampaignProposalPdf(data: any, t: (key: string, opts?: any) => string): void {
  const channelMap: Record<string, string> = { instagram: '인스타그램', blog: '블로그', youtube: '유튜브' };
  const channelIconMap: Record<string, string> = { instagram: '📸', blog: '📝', youtube: '🎬' };
  const influencerAmount = data.rewardAmount ? Math.round(data.rewardAmount * 0.8) : 0;

  // Resolve channels — support both old single `contentChannel` and new multi `contentChannels`
  const channels: string[] = data.contentChannels?.length > 0
    ? data.contentChannels
    : data.contentChannel ? [data.contentChannel] : [];
  const channelsDisplay = channels.map((ch: string) => `${channelIconMap[ch] || ''} ${channelMap[ch] || ch}`).join(', ');

  const escape = (s: string | undefined | null) => {
    if (!s) return '';
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
  };

  const field = (label: string, value: any) => {
    if (!value && value !== 0) return '';
    return `<tr><td class="label">${escape(label)}</td><td class="value">${escape(String(value))}</td></tr>`;
  };

  const textBlock = (label: string, value: string | undefined | null) => {
    if (!value) return '';
    return `<div class="text-block"><div class="text-label">${escape(label)}</div><div class="text-value">${escape(value)}</div></div>`;
  };

  // Build conditions HTML
  const conditionsHtml = (items: any[], labelMap: Record<string, string>) => {
    if (!items?.length) return '';
    return items.map(c => `<span class="badge">${labelMap[c.item] || c.item} × ${c.quantity}</span>`).join(' ');
  };

  // Schedule HTML
  let scheduleHtml = '';
  if (data.recruitmentPeriod?.startDate || data.announcementStartDate || data.visitStartDate) {
    scheduleHtml = '<div class="schedule">';
    if (data.recruitmentPeriod?.startDate) {
      const end = data.recruitmentPeriod.alwaysRecruiting ? '(상시신청)' : data.recruitmentPeriod.endDate || '';
      scheduleHtml += `<div class="sched-row"><span class="sched-label">모집 및 선정 기간</span><span>${data.recruitmentPeriod.startDate} ~ ${end}</span></div>`;
    }
    if (data.announcementStartDate) {
      scheduleHtml += `<div class="sched-row"><span class="sched-label">인플루언서 발표일</span><span>${data.announcementStartDate}${data.announcementEndDate ? ` ~ ${data.announcementEndDate}` : ''}</span></div>`;
    }
    if (data.visitStartDate) {
      scheduleHtml += `<div class="sched-row"><span class="sched-label">방문기간</span><span>${data.visitStartDate}${data.visitEndDate ? ` ~ ${data.visitEndDate}` : ''}</span></div>`;
    }
    if (data.uploadDeadlineDays) {
      scheduleHtml += `<div class="sched-row"><span class="sched-label">업로드 기간</span><span>방문완료 후 ${data.uploadDeadlineDays}일 이내</span></div>`;
    }
    if (data.discussSchedule) {
      scheduleHtml += `<div class="sched-row"><span class="badge-yellow">진행시 상세논의</span></div>`;
    }
    scheduleHtml += '</div>';
  }

  // Secondary submissions table
  let secondaryHtml = '';
  if (data.secondarySubmissions?.length > 0) {
    secondaryHtml = `<table class="cond-table"><thead><tr><th>항목</th><th>수량/분량</th><th>비고</th></tr></thead><tbody>`;
    data.secondarySubmissions.forEach((s: any) => {
      const typeLabel = s.type === 'video' ? '영상' : '사진';
      const unit = s.type === 'video' ? '분' : '개';
      secondaryHtml += `<tr><td>${typeLabel}</td><td>${s.quantity} ${unit}</td><td>${escape(s.note) || '-'}</td></tr>`;
    });
    secondaryHtml += '</tbody></table>';
  }

  // Images
  let imagesHtml = '';
  if (data.representativeImages?.length > 0) {
    imagesHtml = '<div class="images">' + data.representativeImages.map((img: any) =>
      `<img src="${img.fileUrl}" alt="" />`
    ).join('') + '</div>';
  }

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<title>캠페인 제안서 - ${escape(data.campaignName || data.brandName || '')}</title>
<style>
  @page { margin: 15mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Apple SD Gothic Neo', 'Malgun Gothic', 'Noto Sans KR', sans-serif; color: #1a1a1a; font-size: 11px; line-height: 1.6; }
  .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #1e40af; }
  .header h1 { font-size: 24px; color: #1e40af; margin-bottom: 5px; }
  .header .brand { font-size: 18px; font-weight: bold; color: #333; }
  .header .campaign { font-size: 14px; color: #666; margin-top: 3px; }
  .header .date { font-size: 10px; color: #999; margin-top: 8px; }
  .section { margin-bottom: 20px; }
  .section-title { font-size: 14px; font-weight: bold; color: #1e40af; padding-bottom: 5px; border-bottom: 1px solid #ddd; margin-bottom: 10px; }
  table.fields { width: 100%; border-collapse: collapse; }
  table.fields td { padding: 5px 8px; vertical-align: top; border-bottom: 1px solid #f0f0f0; }
  table.fields td.label { width: 140px; color: #666; font-weight: bold; font-size: 10px; }
  table.fields td.value { color: #1a1a1a; }
  .text-block { margin: 8px 0; }
  .text-label { font-size: 10px; font-weight: bold; color: #666; margin-bottom: 3px; }
  .text-value { background: #f9fafb; padding: 8px 10px; border-radius: 4px; white-space: pre-wrap; font-size: 11px; }
  .badge { display: inline-block; padding: 2px 8px; background: #eff6ff; color: #1e40af; border-radius: 10px; font-size: 10px; margin: 2px; }
  .badge-yellow { display: inline-block; padding: 2px 8px; background: #fef3c7; color: #92400e; border-radius: 10px; font-size: 10px; }
  .images { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
  .images img { width: 100px; height: 100px; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; }
  .reward-box { background: #f0f9ff; padding: 10px; border-radius: 6px; margin: 8px 0; }
  .reward-box .main { font-size: 13px; font-weight: bold; }
  .reward-box .sub { font-size: 11px; color: #1e40af; margin-top: 3px; }
  .schedule { margin: 8px 0; }
  .sched-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 11px; border-bottom: 1px solid #f5f5f5; }
  .sched-label { color: #666; font-weight: bold; }
  .cond-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; }
  .cond-table th { background: #f9fafb; padding: 5px 8px; text-align: left; font-weight: bold; color: #666; border-bottom: 1px solid #ddd; }
  .cond-table td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; }
  .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; text-align: center; color: #999; font-size: 9px; }
</style>
</head>
<body>
  <div class="header">
    <h1>캠페인 제안서</h1>
    ${data.brandName ? `<div class="brand">${escape(data.brandName)}</div>` : ''}
    ${data.campaignName ? `<div class="campaign">${escape(data.campaignName)}</div>` : ''}
    <div class="date">${new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
  </div>

  ${imagesHtml}

  <div class="section">
    <div class="section-title">1. 캠페인 기본정보</div>
    <table class="fields">
      ${field('브랜드명', data.brandName)}
      ${field('캠페인명', data.campaignName)}
      ${field('캠페인 카테고리', data.campaignCategory)}
      ${data.clientBrandId ? field('클라이언트 브랜드', data.clientBrandName || data.brandName) : ''}
    </table>
  </div>

  <div class="section">
    <div class="section-title">2. 제공 제품/서비스 정보</div>
    ${textBlock('제품/서비스 소개', data.productIntroduction)}
    <table class="fields">
      ${field('제공 제품/서비스명', data.productName)}
      ${field('제품/서비스 가치', data.providedValueDescription)}
      ${field('제공 방식', data.provisionMethod)}
      ${field('방문 주소', data.visitAddress)}
    </table>
    ${textBlock('방문 및 안내사항', data.additionalNotes)}
  </div>

  <div class="section">
    <div class="section-title">3. 캠페인 미션</div>
    <table class="fields">
      ${field('모집인원', data.numberOfRecruits ? `${data.numberOfRecruits}명` : undefined)}
      ${field('콘텐츠 발행 채널', channelsDisplay || undefined)}
    </table>
    ${data.rewardAmount > 0 ? `
      <div class="reward-box">
        <div class="main">리워드 (제작지원비): $${data.rewardAmount.toLocaleString()}</div>
        <div class="sub">인플루언서 정산 예정 금액: $${influencerAmount.toLocaleString()} (80%)</div>
      </div>
    ` : ''}
    ${data.publishingConditions?.instagram?.conditions?.length ? `<div style="margin:8px 0"><strong style="font-size:10px;color:#666">📸 인스타그램 발행조건</strong><br/>${conditionsHtml(data.publishingConditions.instagram.conditions, { reels: '릴스', story: '스토리', post_feed: '포스트/피드' })}</div>` : ''}
    ${data.publishingConditions?.blog?.conditions?.length ? `<div style="margin:8px 0"><strong style="font-size:10px;color:#666">📝 블로그 발행조건</strong><br/>${conditionsHtml(data.publishingConditions.blog.conditions, { post: '포스트' })}</div>` : ''}
    ${data.publishingConditions?.youtube?.conditions?.length ? `<div style="margin:8px 0"><strong style="font-size:10px;color:#666">🎬 유튜브 발행조건</strong><br/>${conditionsHtml(data.publishingConditions.youtube.conditions, { video: '영상', shorts: '쇼츠' })}</div>` : ''}
    <table class="fields">
      ${field('콘텐츠 내 삽입 링크', data.contentInsertLink)}
    </table>
    ${scheduleHtml}
    ${textBlock('콘텐츠 작성 가이드', data.contentWritingRequirements)}
    ${data.secondarySubmissions?.length ? `<div style="margin:8px 0"><strong style="font-size:10px;color:#666">2차 활용 제출 항목</strong>${secondaryHtml}</div>` : ''}
    <table class="fields">
      ${field('제출경로', data.submissionPath)}
    </table>
    ${textBlock('2차 활용 제출 요청 가이드', data.secondarySubmissionGuide)}
  </div>

  <div class="footer">
    <p>MatchGo Campaign Management System</p>
    <p>${new Date().toISOString()}</p>
  </div>
</body>
</html>`;

  // Open print window
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('팝업이 차단되었습니다. 팝업을 허용해주세요.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
